
import { Award, BookOpen, Database, TrendingUp, Star } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface EvidenceQualityMeterProps {
  evidenceLevel: 'high' | 'medium' | 'low';
  studyCount: number;
  confidence: number;
  sources: string[];
  showDetails?: boolean;
  className?: string;
}

const EvidenceQualityMeter = ({
  evidenceLevel,
  studyCount,
  confidence,
  sources,
  showDetails = true,
  className = ""
}: EvidenceQualityMeterProps) => {
  const getQualityConfig = (level: 'high' | 'medium' | 'low') => {
    switch (level) {
      case 'high':
        return {
          color: 'green',
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          borderColor: 'border-green-200',
          icon: Award,
          label: 'High Quality',
          description: 'Systematic reviews, meta-analyses, RCTs',
          score: 90
        };
      case 'medium':
        return {
          color: 'yellow',
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-800',
          borderColor: 'border-yellow-200',
          icon: BookOpen,
          label: 'Moderate Quality',
          description: 'Cohort studies, case-control studies',
          score: 70
        };
      case 'low':
        return {
          color: 'orange',
          bgColor: 'bg-orange-100',
          textColor: 'text-orange-800',
          borderColor: 'border-orange-200',
          icon: Database,
          label: 'Limited Quality',
          description: 'Case reports, expert opinions',
          score: 50
        };
    }
  };

  const config = getQualityConfig(evidenceLevel);
  const IconComponent = config.icon;

  return (
    <Card className={`p-6 bg-white/90 backdrop-blur-sm ${config.borderColor} border-2 ${className}`}>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`${config.bgColor} p-3 rounded-lg`}>
              <IconComponent className={`h-6 w-6 ${config.textColor}`} />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Evidence Quality</h3>
              <Badge className={`${config.bgColor} ${config.textColor} border-none`}>
                {config.label}
              </Badge>
            </div>
          </div>
          <div className="text-right">
            <div className={`text-2xl font-bold ${config.textColor}`}>
              {config.score}
            </div>
            <div className="text-xs text-gray-500">Quality Score</div>
          </div>
        </div>

        {/* Quality Meter */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-gray-600">
            <span>Quality Level</span>
            <span>{config.score}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`bg-${config.color}-500 h-2 rounded-full transition-all duration-300`}
              style={{ width: `${config.score}%` }}
            />
          </div>
        </div>

        {showDetails && (
          <>
            {/* Study Count and Confidence */}
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{studyCount}</div>
                <div className="text-xs text-gray-600">Studies</div>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{confidence}%</div>
                <div className="text-xs text-gray-600">Confidence</div>
              </div>
            </div>

            {/* Sources */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Data Sources:</h4>
              <div className="flex flex-wrap gap-1">
                {sources.map((source, index) => (
                  <Badge key={index} variant="outline" className="text-xs">
                    {source}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Description */}
            <p className="text-xs text-gray-600 italic">
              {config.description}
            </p>
          </>
        )}
      </div>
    </Card>
  );
};

export default EvidenceQualityMeter;
